/**
 * Effects Chain — Reverb, Filter, and Master Compressor
 */

export class EffectsChain {
  constructor(audioContext) {
    this.ctx = audioContext
  }

  /**
   * Create a reverb wet/dry parallel node cluster.
   * Returns { input, output, setWet(0-1) }
   */
  createReverb(roomSize = 2.5) {
    const convolver = this.ctx.createConvolver()
    convolver.buffer = this._buildImpulse(roomSize)

    const input = this.ctx.createGain()
    const dry   = this.ctx.createGain()
    const wet   = this.ctx.createGain()
    const output = this.ctx.createGain()

    dry.gain.value = 1.0
    wet.gain.value = 0.0

    input.connect(dry)
    input.connect(convolver)
    convolver.connect(wet)
    dry.connect(output)
    wet.connect(output)

    return {
      input,
      output,
      setWet(amount) {        // amount 0–1
        dry.gain.value = 1 - amount * 0.7   // keep some dry
        wet.gain.value = amount
      },
    }
  }

  /**
   * Create a filter node (lowpass ↔ highpass sweep).
   * value -1 = full lowpass 80Hz, 0 = allpass (bypass), +1 = full highpass 18kHz
   */
  createFilter() {
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 22050   // effectively bypassed
    filter.Q.value = 0.7

    return {
      node: filter,
      setValue(value) {   // value -1 to +1
        if (value < 0) {
          // Lowpass sweep: -1 → 80Hz, 0 → 22050Hz
          filter.type = 'lowpass'
          const norm = value + 1              // 0..1
          filter.frequency.value = 80 + norm * (22050 - 80)
        } else if (value > 0) {
          // Highpass sweep: 0 → 20Hz, +1 → 18000Hz
          filter.type = 'highpass'
          filter.frequency.value = 20 + value * 17980
        } else {
          filter.type = 'lowpass'
          filter.frequency.value = 22050
        }
      },
    }
  }

  /**
   * Create a master bus compressor with sensible broadcast-quality settings.
   */
  createMasterCompressor() {
    const comp = this.ctx.createDynamicsCompressor()
    comp.threshold.value = -18
    comp.knee.value      = 6
    comp.ratio.value     = 4
    comp.attack.value    = 0.003
    comp.release.value   = 0.25
    return comp
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  _buildImpulse(duration = 2.5, decay = 2.0) {
    const sr     = this.ctx.sampleRate
    const length = Math.floor(sr * duration)
    const buf    = this.ctx.createBuffer(2, length, sr)

    for (let ch = 0; ch < 2; ch++) {
      const data = buf.getChannelData(ch)
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay)
      }
    }
    return buf
  }
}
